import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiCode, 
  FiPlus, 
  FiChevronDown,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiX,
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiAlertTriangle,
  FiFileText,
  FiLock,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiTag,
  FiActivity,
  FiUpload,
  FiSave,
  FiEdit3
} from 'react-icons/fi';
import LaboratoryOrdersService from '../../../PatientTreatmentDetails/services/laboratoryOrdersService';

const LaboratoryTreatments = ({ patientId, onCountUpdate, componentName = 'LaboratoryTreatments' }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showMostRecentDropdown, setShowMostRecentDropdown] = useState(false);
  const [generalNotes, setGeneralNotes] = useState('');
  
  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estados para subir resultados - CORREGIDOS
  const [showResultsOffcanvas, setShowResultsOffcanvas] = useState(false);
  const [resultsData, setResultsData] = useState({});
  const [isUploadingResults, setIsUploadingResults] = useState(false);
  const [resultsErrors, setResultsErrors] = useState({});
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    testType: '',
    lastUpdate: '',
    orderDate: '',
    endDate: '',
    status: 'All',
    priority: 'All'
  });

  // Función mejorada para debuggear hasResults
  const hasResults = (order) => {
    console.log('🔍 Rendering results for order:', order);
    const originalOrder = order._original || order;
    
    // Si la orden está completada, mostrar sección de resultados
    if (order.status === 'completed' || originalOrder.status === 'completed') {
      return (
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="card-title mb-0">
                <FiCheck className="me-2 text-success" />
                Results
              </h6>
              <span className="badge bg-success">
                Completed {order.completed_date ? `on ${formatDateTime(order.completed_date)}` : ''}
              </span>
            </div>
            <div className="card-body">
              {/* Renderizar resultados si están disponibles */}
              {originalOrder.results && Array.isArray(originalOrder.results) && originalOrder.results.length > 0 ? (
                <div className="results-container">
                  <h6 className="text-primary mb-3">Test Results</h6>
                  {originalOrder.results.map((testResult, testIndex) => (
                    <div key={testIndex} className="test-result-card border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="text-primary mb-0">
                          {testResult.testName || `Test ${testIndex + 1}`}
                        </h6>
                        {testResult.testCode && (
                          <span className="badge bg-secondary">{testResult.testCode}</span>
                        )}
                      </div>
                      
                      {testResult.parameters && Array.isArray(testResult.parameters) && (
                        <div className="parameters-results">
                          <div className="row g-2">
                            {testResult.parameters.map((param, paramIndex) => (
                              <div key={paramIndex} className="col-md-6">
                                <div className="parameter-result p-2 border rounded bg-light">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <div className="fw-medium">{param.parameterName}</div>
                                      <div className="result-value fs-5 text-primary fw-bold">
                                        {param.value} {param.unit && <span className="text-muted">{param.unit}</span>}
                                      </div>
                                      {param.referenceRange && (param.referenceRange.min || param.referenceRange.max) && (
                                        <small className="text-muted">
                                          Range: {param.referenceRange.min || 'N/A'} - {param.referenceRange.max || 'N/A'}
                                        </small>
                                      )}
                                    </div>
                                    <div className="text-end">
                                      {param.isAbnormal ? (
                                        <span className="badge bg-warning">Abnormal</span>
                                      ) : (
                                        <span className="badge bg-success">Normal</span>
                                      )}
                                    </div>
                                  </div>
                                  {param.notes && (
                                    <div className="mt-2">
                                      <small className="text-info">
                                        <FiInfo className="me-1" />
                                        {param.notes}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {testResult.generalNotes && (
                        <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                          <small className="text-info">
                            <FiFileText className="me-1" />
                            {testResult.generalNotes}
                          </small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // Fallback: mostrar que está completada pero sin detalles de resultados
                <div className="alert alert-info">
                  <FiInfo className="me-2" />
                  <strong>Order Completed</strong>
                  <p className="mb-0 mt-2">
                    This order has been marked as completed. Results may be available in the laboratory system 
                    or may need to be uploaded manually.
                  </p>
                  {order.completed_date && (
                    <small className="text-muted">
                      Completed on {formatDateTime(order.completed_date)}
                      {order.processed_by && ` by ${order.processed_by}`}
                    </small>
                  )}
                </div>
              )}
              
              {/* Notas generales de resultados */}
              {(order.results_notes || originalOrder.observations) && (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6 className="text-primary mb-2">
                    <FiFileText className="me-2" />
                    Additional Notes
                  </h6>
                  <p className="mb-0">{order.results_notes || originalOrder.observations}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Helper function to get results completion percentage
  const getResultsCompletionPercentage = (order) => {
    const originalOrder = order._original || order;
    let totalParams = 0;
    let completedParams = 0;
    
    if (originalOrder.selectedTests && Array.isArray(originalOrder.selectedTests)) {
      originalOrder.selectedTests.forEach(test => {
        if (test.selectedParameters && Array.isArray(test.selectedParameters)) {
          totalParams += test.selectedParameters.length;
          completedParams += test.selectedParameters.filter(param => 
            param.result !== undefined && param.result !== null && param.result !== ''
          ).length;
        }
      });
    }
    
    return totalParams > 0 ? Math.round((completedParams / totalParams) * 100) : 0;
  };

  // Improved helper function to extract categories and test info from order
  const extractTestInfoFromOrder = (order) => {
    console.log('Extracting test info from order:', order);
    
    // Handle new order structure with selectedTests array
    if (order.selectedTests && Array.isArray(order.selectedTests)) {
      const categories = order.selectedTests
        .map(test => test.category || test.testCategory)
        .filter(category => category && category.trim() !== '')
        .filter((category, index, arr) => arr.indexOf(category) === index);
      
      const testNames = order.selectedTests
        .map(test => test.testName || test.test_name || test.name)
        .filter(name => name && name.trim() !== '');
      
      // Count total selected parameters across all tests
      const totalSelectedParameters = order.selectedTests.reduce((total, test) => {
        return total + (test.selectedParameters ? test.selectedParameters.length : 0);
      }, 0);
      
      return {
        categories: categories.length > 0 ? categories.join(', ') : (order.test_type || order.type || 'Unknown'),
        testNames: testNames.length > 0 ? testNames.join(', ') : (order.test_name || order.name || 'Unknown Test'),
        testCount: order.selectedTests.length,
        totalParameters: totalSelectedParameters,
        hasDetailedParameters: order.selectedTests.some(test => 
          test.selectedParameters && test.selectedParameters.length > 0
        )
      };
    }
    
    // Fallback for legacy structure
    return {
      categories: order.test_type || order.type || 'Unknown',
      testNames: order.test_name || order.name || order.description || 'Unknown Test',
      testCount: 1,
      totalParameters: 0,
      hasDetailedParameters: false
    };
  };

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS CON CALLBACK**
  const loadOrders = useCallback(async () => {
    if (!patientId) return;
    
    setIsLoading(true);
    try {
      const response = await LaboratoryOrdersService.getPatientOrders(patientId);
      console.log('Raw API response:', response);
      
      let ordersData = [];
      
      if (response.data) {
        ordersData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (response.orders) {
        ordersData = Array.isArray(response.orders) ? response.orders : [response.orders];
      }

      const normalizedOrders = ordersData.map(order => {
        console.log('Processing order:', order);
        
        const testInfo = extractTestInfoFromOrder(order);
        
        // Handle patient information (could be populated or just ID)
        const patientInfo = typeof order.patientId === 'object' && order.patientId 
          ? { id: order.patientId._id, name: order.patientId.name }
          : { id: order.patientId || patientId, name: 'Unknown Patient' };

        // Handle createdBy information (could be populated or just ID)
        const createdByInfo = typeof order.createdBy === 'object' && order.createdBy
          ? { id: order.createdBy._id, name: order.createdBy.name, email: order.createdBy.email }
          : { id: order.createdBy || order.orderedById, name: order.orderedByName || 'Unknown', email: '' };

        return {
          // Primary identifiers
          order_id: order._id || order.order_id || order.id,
          order_number: order.orderNumber || order.order_number || `LAB-${Date.now()}`,
          sample_id: order.sample_id || order.specimen_id || null,
          
          // Test information
          test_type: testInfo.categories,
          test_name: testInfo.testNames,
          test_count: testInfo.testCount,
          
          // Status and priority
          status: order.status || 'pending',
          priority: order.priority || (order.isUrgent ? 'urgent' : 'routine'),
          is_urgent: order.isUrgent || order.is_urgent || false,
          
          // Dates
          order_date: order.createdAt || order.order_date || order.created_date || order.orderDate,
          expected_results_date: order.expectedResultsDate || order.expected_results_date || order.expected_date,
          completed_date: order.completed_date || order.result_date || null,
          last_updated: order.updatedAt || order.last_updated || order.modified_date || order.createdAt,
          created_date: order.createdAt || order.created_date,
          updated_date: order.updatedAt || order.updated_date,
          
          // Personnel
          ordered_by: createdByInfo.name,
          ordered_by_id: createdByInfo.id,
          ordered_by_email: createdByInfo.email,
          processed_by: order.processed_by || order.technician || null,
          reviewed_by: order.reviewed_by || order.pathologist || null,
          
          // Additional data
          type: order.type || 'Laboratory Order',
          requesting_team: order.requestingTeam || order.requesting_team || 'Unknown',
          reason: order.reason || '',
          clinical_data: order.clinicalData || order.clinical_data || '',
          department: order.department || 'laboratory',
          
          // Results and samples
          results: order.results || [],
          samples: order.samples || [],
          results_notes: order.results_notes || order.notes || '',
          reference_ranges: order.reference_ranges || {},
          critical_values: order.critical_values || false,
          
          // Templates and sub-exams
          templates: order.templates || [],
          sub_exams: order.sub_exams || {},
          
          // Patient info
          patient_id: patientInfo.id,
          patient_name: patientInfo.name,
          
          // Preserve original for detailed view
          _original: order
        };
      });

      console.log('Normalized orders:', normalizedOrders);
      
      // **IMPORTANTE: Solo actualizar si el contador cambió**
      if (orders.length !== normalizedOrders.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${orders.length} a ${normalizedOrders.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedOrders.length);
      }
      
      setOrders(normalizedOrders);
    } catch (error) {
      console.error('Error loading laboratory orders:', error);
      setOrders([]);
      // También actualizar contador en caso de error
      if (orders.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, orders.length, onCountUpdate, componentName]);

  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadOrdersStats = useCallback(async () => {
    if (!patientId || orders.length === 0) return;
    
    try {
      const response = await LaboratoryOrdersService.getOrdersStats();
      console.log('Stats response:', response);
      
      const pendingCount = orders.filter(order => 
        order.status === 'pending'
      ).length;
      
      const criticalCount = orders.filter(order => 
        order.critical_values === true
      ).length;
      
      if (pendingCount > 0 || criticalCount > 0) {
        setAlertInfo({
          alert_type: 'laboratory',
          message: `${pendingCount} pending results, ${criticalCount} critical values`,
          priority: criticalCount > 0 ? 'high' : 'medium',
          requires_action: true
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading orders stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, orders]);

  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadOrders();
    }
  }, [patientId, loadOrders]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (orders.length > 0) {
      loadOrdersStats();
    }
  }, [orders, loadOrdersStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${orders.length}`);
    onCountUpdate && onCountUpdate(componentName, orders.length);
  }, [orders.length, onCountUpdate, componentName]);

  // Apply filters whenever orders or filters change
  useEffect(() => {
    applyFilters();
  }, [orders, filters, globalFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowMostRecentDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showResultsOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showResultsOffcanvas]);

  const applyFilters = () => {
    let filtered = [...orders];

    if (globalFilter) {
      filtered = filtered.filter(order => 
        order.test_name.toLowerCase().includes(globalFilter.toLowerCase()) ||
        order.test_type.toLowerCase().includes(globalFilter.toLowerCase()) ||
        (order.sample_id && order.sample_id.toLowerCase().includes(globalFilter.toLowerCase())) ||
        (order.order_number && order.order_number.toLowerCase().includes(globalFilter.toLowerCase()))
      );
    }

    if (filters.status !== 'All') {
      const statusValue = filters.status.toLowerCase().replace(' ', '_');
      filtered = filtered.filter(order => 
        order.status.toLowerCase() === statusValue
      );
    }

    if (filters.priority !== 'All') {
      filtered = filtered.filter(order => 
        order.priority.toLowerCase() === filters.priority.toLowerCase()
      );
    }

    if (filters.orderDate) {
      filtered = filtered.filter(order => {
        const orderDate = order.order_date;
        return orderDate && new Date(orderDate) >= new Date(filters.orderDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(order => {
        const orderDate = order.order_date;
        return orderDate && new Date(orderDate) <= new Date(filters.endDate);
      });
    }

    if (filters.lastUpdate) {
      filtered = filtered.filter(order => {
        const lastUpdated = order.last_updated || order.updated_date || order.order_date;
        
        if (!lastUpdated) return false;
        
        try {
          const updateDate = new Date(lastUpdated).toISOString().split('T')[0];
          return updateDate === filters.lastUpdate;
        } catch {
          return false;
        }
      });
    }

    setFilteredOrders(filtered);
  };

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleViewOrder = (orderId) => {
    console.log('Viewing order:', orderId);
    const order = orders.find(o => o.order_id === orderId);
    if (order) {
      console.log('Found order for viewing:', order);
      setSelectedOrder(order);
      setShowViewOffcanvas(true);
    } else {
      console.error('Order not found:', orderId);
    }
  };

  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    setShowAddOffcanvas(true);
  };

  // **FUNCIÓN ACTUALIZADA PARA MANEJAR NUEVAS ÓRDENES Y ACTUALIZAR CONTADOR**
  const handleSubmitNewOrder = async (orderData) => {
    console.log('Submitting new order:', orderData);
    setIsSubmitting(true);
    try {
      const result = await LaboratoryOrdersService.createOrder(orderData);
      console.log('Order created:', result);
      
      // Recargar datos para obtener el nuevo contador
      await loadOrders();
      await loadOrdersStats();
      
      setShowAddOffcanvas(false);
      
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // FUNCIÓN CORREGIDA - handleUploadResults
  const handleUploadResults = (orderId) => {
    console.log('🔧 Opening upload results for order:', orderId);
    const order = orders.find(o => o.order_id === orderId);
    
    if (order) {
      console.log('✅ Found order:', order);
      setSelectedOrder(order);
      // Limpiar estados previos
      setUploadError('');
      setUploadSuccess('');
      setResultsErrors({});
      // Inicializar datos
      initializeResultsData(order);
      setShowResultsOffcanvas(true);
    } else {
      console.error('❌ Order not found:', orderId);
      console.log('Available orders:', orders.map(o => ({ id: o.order_id, number: o.order_number })));
      alert('Order not found. Please refresh and try again.');
    }
  };

  // FUNCIÓN CORREGIDA - initializeResultsData
  const initializeResultsData = (order) => {
    console.log('🔧 Initializing results data for order:', order);
    const originalOrder = order._original || order;
    const initialData = {};
    
    if (originalOrder.selectedTests && Array.isArray(originalOrder.selectedTests)) {
      originalOrder.selectedTests.forEach((test, testIndex) => {
        if (test.selectedParameters && Array.isArray(test.selectedParameters)) {
          test.selectedParameters.forEach((param, paramIndex) => {
            const key = `${testIndex}-${paramIndex}`;
            initialData[key] = {
              testIndex,
              paramIndex,
              testName: test.testName || test.test_name || test.name || 'Unknown Test',
              parameterName: param.parameterName || param.name || 'Unknown Parameter',
              value: param.result || '',
              unit: param.unit || '',
              isAbnormal: param.isAbnormal || false,
              notes: param.notes || '',
              referenceRange: param.referenceRange || {}
            };
            console.log(`✅ Added parameter ${key}:`, initialData[key]);
          });
        }
      });
    }
    
    console.log('📊 Final initialized data:', initialData);
    console.log('📊 Total parameters to fill:', Object.keys(initialData).length);
    
    setResultsData(initialData);
    setResultsErrors({});
  };

  // FUNCIÓN CORREGIDA - handleResultChange (reemplaza las llamadas a handleResultValueChange)
const handleResultChange = (key, field, value) => {
  console.log(`🔄 Updating ${key}.${field} to:`, value);
  
  setResultsData(prev => ({
    ...prev,
    [key]: {
      ...prev[key],
      [field]: value
    }
  }));
  
  // Clear error for this field
  if (resultsErrors[key]?.[field]) {
    setResultsErrors(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: ''
      }
    }));
  }
  
  // Clear general upload error
  if (uploadError) {
    setUploadError('');
  }
};

  // FUNCIÓN ACTUALIZADA - validateResults (mejorada)
const validateResults = () => {
  console.log('🔍 Validating results:', resultsData);
  const errors = {};
  let isValid = true;
  
  // Check if we have any data to validate
  if (Object.keys(resultsData).length === 0) {
    console.error('❌ No results data to validate');
    setUploadError('No parameters found to validate. Please refresh and try again.');
    return false;
  }
  
  Object.keys(resultsData).forEach(key => {
    const result = resultsData[key];
    const fieldErrors = {};
    
    // Validate required fields
    if (!result.value || String(result.value).trim() === '') {
      fieldErrors.value = 'Result value is required';
      isValid = false;
      console.log(`❌ Missing value for ${key}:`, result);
    } else {
      // Validate numeric values if needed
      const value = String(result.value).trim();
      if (result.unit && (
        result.unit.toLowerCase().includes('μl') || 
        result.unit.toLowerCase().includes('mg/dl') ||
        result.unit.toLowerCase().includes('mmol/l') ||
        result.unit.toLowerCase().includes('iu/l')
      )) {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          fieldErrors.value = 'Invalid numeric value';
          isValid = false;
        }
      }
    }
    
    // Validate status
    if (result.status && !['normal', 'abnormal', 'critical', 'pending'].includes(result.status)) {
      fieldErrors.status = 'Invalid status value';
      isValid = false;
    }
    
    if (Object.keys(fieldErrors).length > 0) {
      errors[key] = fieldErrors;
    }
  });
  
  console.log('🔍 Validation results:', { isValid, errors });
  setResultsErrors(errors);
  
  if (!isValid) {
    const errorCount = Object.keys(errors).length;
    setUploadError(`Please fix ${errorCount} validation error(s) before submitting.`);
  }
  
  return isValid;
};

const handleSubmitResults = async () => {
  console.log('🚀 Starting submit results process...');
  
  setUploadError('');
  setUploadSuccess('');
  
  if (!validateResults()) {
    console.error('❌ Validation failed, aborting submit');
    return;
  }
  
  if (!selectedOrder.order_id) {
    setUploadError('Order ID is missing. Please refresh and try again.');
    return;
  }
  
  setIsUploadingResults(true);
  
  try {
    const originalOrder = selectedOrder._original || selectedOrder;
    console.log('🔍 Original order structure:', originalOrder);
    
    // CORRECCIÓN: Transformar según testResultSchema del backend
    const testResultsMap = new Map();
    
    // Agrupar parámetros por test
    Object.values(resultsData).forEach(result => {
      const testKey = result.testIndex;
      
      if (!testResultsMap.has(testKey)) {
        const testInfo = originalOrder.selectedTests?.[result.testIndex];
        console.log(`🔍 Test info for index ${testKey}:`, testInfo);
        
        // IMPORTANTE: Usar testTemplateId correctamente
        const testTemplateId = testInfo?.testTemplateId || testInfo?._id;
        
        if (!testTemplateId) {
          console.error(`❌ No testTemplateId found for test ${testKey}`);
          throw new Error(`Missing testTemplateId for test: ${result.testName}`);
        }
        
        testResultsMap.set(testKey, {
          testTemplateId: testTemplateId,
          testName: result.testName || testInfo?.testName || testInfo?.name,
          testCode: testInfo?.testCode || testInfo?.code || '',
          category: testInfo?.category || '',
          parameters: [],
          generalNotes: ''
        });
      }
      
      // Obtener información del parámetro original
      const testInfo = originalOrder.selectedTests?.[result.testIndex];
      const originalParam = testInfo?.selectedParameters?.[result.paramIndex];
      
      // Agregar parámetro con estructura parameterResultSchema
      testResultsMap.get(testKey).parameters.push({
        parameterId: originalParam?.parameterId || `${testKey}-param-${result.paramIndex}`,
        parameterName: result.parameterName,
        loincCode: originalParam?.loincCode || '',
        value: String(result.value), // Asegurar que sea string
        unit: result.unit || originalParam?.unit || '',
        referenceRange: result.referenceRange || originalParam?.referenceRange || {},
        isAbnormal: result.status === 'abnormal' || result.status === 'critical',
        notes: result.notes || ''
      });
    });
    
    const results = Array.from(testResultsMap.values());
    
    // Validar que tenemos al menos un resultado
    if (results.length === 0) {
      throw new Error('No results to submit');
    }
    
    // Validar que cada resultado tiene testTemplateId
    results.forEach((result, index) => {
      if (!result.testTemplateId) {
        throw new Error(`Result ${index} is missing testTemplateId`);
      }
      if (!result.parameters || result.parameters.length === 0) {
        throw new Error(`Result ${index} has no parameters`);
      }
    });
    
    // Payload que espera el controlador updateOrderResults
    const resultsPayload = {
      results: results,
      observations: generalNotes.trim() || '',
      status: 'completed'
    };
    
    console.log('📤 Final payload:', JSON.stringify(resultsPayload, null, 2));
    console.log('📤 Results count:', results.length);
    console.log('📤 Total parameters:', results.reduce((sum, r) => sum + r.parameters.length, 0));
    console.log('📤 Sending to order ID:', selectedOrder.order_id);
    
    const response = await LaboratoryOrdersService.updateOrderResults(
      selectedOrder.order_id, 
      resultsPayload
    );
    
    console.log('✅ Success response:', response);
    
    // Update local state
    const updatedOrders = orders.map(order => {
      if (order.order_id === selectedOrder.order_id) {
        return {
          ...order,
          status: 'completed',
          results: results,
          completed_date: new Date().toISOString(),
          results_notes: generalNotes.trim() || null,
          last_updated: new Date().toISOString()
        };
      }
      return order;
    });
    setOrders(updatedOrders);
    
    setUploadSuccess(`Results uploaded successfully! ${results.length} tests with ${Object.keys(resultsData).length} parameters processed.`);
    
    // Auto-close and refresh
    setTimeout(async () => {
      try {
        await loadOrders();
        await loadOrdersStats();
      } catch (refreshError) {
        console.warn('⚠️ Warning: Could not refresh data:', refreshError);
      }
      
      setShowResultsOffcanvas(false);
      
      // Show updated order
      setTimeout(() => {
        const updatedOrder = orders.find(o => o.order_id === selectedOrder.order_id);
        if (updatedOrder) {
          setSelectedOrder(updatedOrder);
          setShowViewOffcanvas(true);
        }
      }, 300);
    }, 2000);
    
  } catch (error) {
    console.error('❌ Complete error:', error);
    
    let errorMessage = 'Failed to upload results. ';
    
    if (error.response?.data) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          errorMessage += `Validation error: ${data.message || 'Invalid data format'}`;
          if (data.resultReceived) {
            console.error('Bad result structure:', data.resultReceived);
          }
          if (data.parameterReceived) {
            console.error('Bad parameter structure:', data.parameterReceived);
          }
          break;
        case 401:
          errorMessage += 'Authentication required. Please log in again.';
          break;
        case 404:
          errorMessage += 'Order not found. Please refresh and try again.';
          break;
        case 500:
          errorMessage += 'Server error. Please try again later.';
          break;
        default:
          errorMessage += data.message || `Server error (${status})`;
      }
    } else if (error.request) {
      errorMessage += 'Network error. Check your connection.';
    } else {
      errorMessage += error.message || 'Unknown error occurred.';
    }
    
    setUploadError(errorMessage);
    
  } finally {
    setIsUploadingResults(false);
  }
};

  const handleMostRecentAction = (action) => {
    setShowMostRecentDropdown(false);
    switch (action) {
      case 'sendMail':
        console.log('Send mail action');
        break;
      case 'sendMessage':
        console.log('Send message action');
        break;
      case 'downloadReport':
        console.log('Download report action');
        break;
      default:
        break;
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      testType: '',
      lastUpdate: '',
      orderDate: '',
      endDate: '',
      status: 'All',
      priority: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedOrder(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };
  
  // FUNCIÓN ACTUALIZADA - handleCloseResultsOffcanvas (para limpiar generalNotes)
const handleCloseResultsOffcanvas = () => {
  if (!isUploadingResults) {
    setShowResultsOffcanvas(false);
    setSelectedOrder(null);
    setResultsData({});
    setResultsErrors({});
    setUploadError('');
    setUploadSuccess('');
    setGeneralNotes(''); // Limpiar las notas generales
  }
};

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'processing':
        return 'warning';
      case 'pending':
        return 'secondary';
      case 'cancelled':
        return 'danger';
      case 'critical':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <FiCheck />;
      case 'in_progress':
      case 'processing':
        return <FiActivity />;
      case 'pending':
        return <FiClock />;
      case 'cancelled':
        return <FiX />;
      case 'critical':
        return <FiAlertTriangle />;
      default:
        return <FiActivity />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'routine':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  // Helper function to render detailed test information with results
  const renderDetailedTestInfo = (order, showResults = false) => {
    console.log('🔍 DEBUG - Rendering detailed test info for order:', order);
    const originalOrder = order._original || order;
    
    // Check if we have the new detailed test structure with selectedTests
    if (originalOrder && originalOrder.selectedTests && Array.isArray(originalOrder.selectedTests)) {
      console.log('✅ Found selectedTests array with', originalOrder.selectedTests.length, 'tests');
      
      return originalOrder.selectedTests.map((test, index) => {
        let parametersToShow = [];
        let hasSelectedParameters = false;
        
        if (test.selectedParameters && Array.isArray(test.selectedParameters) && test.selectedParameters.length > 0) {
          parametersToShow = test.selectedParameters;
          hasSelectedParameters = true;
        }
        
        return (
          <div key={index} className="test-detail-card border rounded p-3 mb-3">
            <div className="row">
              <div className="col-12">
                {/* Test Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="flex-grow-1">
                    <h6 className="text-primary mb-1">
                      {test.testName || test.test_name || test.name || 'Unknown Test'}
                    </h6>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <span className="badge bg-secondary">
                        {test.testCode || test.code || 'N/A'}
                      </span>
                      {test.templateLoincCode && (
                        <span className="badge bg-info">
                          <FiCode className="me-1" size={12} />
                          LOINC: {test.templateLoincCode}
                        </span>
                      )}
                      <span className="badge bg-success">
                        {test.category || test.testCategory || test.type || 'N/A'}
                      </span>
                      <span className={`badge bg-${getPriorityColor(test.priority)}`}>
                        {test.priority || 'normal'}
                      </span>
                    </div>
                  </div>
                  <div className="text-end">
                    {test.allParametersSelected && (
                      <span className="badge bg-primary">All Parameters</span>
                    )}
                    {hasSelectedParameters && (
                      <span className="badge bg-success">
                        {parametersToShow.length} Selected
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Parameters Section */}
                {hasSelectedParameters && parametersToShow.length > 0 ? (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted fw-bold">Parameters</small>
                      {showResults && (
                        <small className="text-success">
                          <FiCheck className="me-1" />
                          Results Available
                        </small>
                      )}
                    </div>
                    <div className="row g-2">
                      {parametersToShow.map((param, paramIndex) => (
                        <div key={paramIndex} className="col-12">
                          <div className="parameter-item p-2 border rounded-2 bg-light">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="fw-medium text-dark">
                                  {param.parameterName || param.name || 'Unknown Parameter'}
                                </div>
                                {param.parameterCode && (
                                  <small className="text-muted">
                                    Code: {param.parameterCode}
                                  </small>
                                )}
                                {param.loincCode && (
                                  <div>
                                    <small className="text-info">
                                      <FiCode className="me-1" size={10} />
                                      LOINC: {param.loincCode}
                                    </small>
                                  </div>
                                )}
                              </div>
                              <div className="text-end">
                                {param.unit && (
                                  <small className="badge bg-light text-dark border">
                                    {param.unit}
                                  </small>
                                )}
                                {showResults && param.result !== undefined && param.result !== null && param.result !== '' && (
                                  <div className="mt-1">
                                    <span className={`badge ${param.isAbnormal ? 'bg-warning' : 'bg-success'}`}>
                                      {param.result} {param.unit || ''}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {param.referenceRange && (
                              <div className="mt-1">
                                <small className="text-muted">
                                  Range: {param.referenceRange.min || 'N/A'} - {param.referenceRange.max || 'N/A'}
                                </small>
                              </div>
                            )}
                            {showResults && param.notes && (
                              <div className="mt-1">
                                <small className="text-info">
                                  <FiInfo className="me-1" size={10} />
                                  {param.notes}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <div className="alert alert-info py-2 mb-0">
                      <small>
                        <FiInfo className="me-1" />
                        No specific parameters selected - All standard parameters will be included
                      </small>
                    </div>
                  </div>
                )}
                
                {/* Additional Test Info */}
                {(test.description || test.instructions || test.notes) && (
                  <div className="mt-3 pt-2 border-top">
                    {test.description && (
                      <div className="mb-2">
                        <small className="text-muted fw-bold">Description:</small>
                        <div><small>{test.description}</small></div>
                      </div>
                    )}
                    {test.instructions && (
                      <div className="mb-2">
                        <small className="text-muted fw-bold">Instructions:</small>
                        <div><small>{test.instructions}</small></div>
                      </div>
                    )}
                    {test.notes && (
                      <div className="mb-2">
                        <small className="text-muted fw-bold">Notes:</small>
                        <div><small>{test.notes}</small></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      });
    }
    
    // Fallback for legacy structure
    console.log('⚠️ Using fallback rendering for legacy order structure');
    return (
      <div className="alert alert-warning">
        <FiAlertTriangle className="me-2" />
        <strong>Legacy Order Structure:</strong>
        <div className="mt-2">
          <div><strong>Type:</strong> {order.test_type || order.type || 'Unknown'}</div>
          <div><strong>Test:</strong> {order.test_name || order.name || 'Unknown Test'}</div>
          {order.description && <div><strong>Description:</strong> {order.description}</div>}
        </div>
      </div>
    );
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'order_number',
      header: 'Order #',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.order_number || 'N/A'}
            </div>
            <small className="text-muted">
              {row.original.sample_id || 'No sample ID'}
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'test_name',
      header: 'Test Information',
      size: 200,
      Cell: ({ row }) => {
        const testInfo = extractTestInfoFromOrder(row.original);
        const completionPercentage = getResultsCompletionPercentage(row.original);
        const orderHasResults = hasResults(row.original);
        
        return (
          <div>
            <div className="fw-medium text-dark">
              {testInfo.testNames}
            </div>
            <div className="d-flex flex-wrap gap-1 mt-1">
              <span className="badge bg-secondary">
                {testInfo.categories}
              </span>
              {testInfo.testCount > 1 && (
                <span className="badge bg-info">
                  {testInfo.testCount} tests
                </span>
              )}
              {testInfo.totalParameters > 0 && (
                <span className="badge bg-primary">
                  {testInfo.totalParameters} params
                </span>
              )}
            </div>
            {orderHasResults && (
              <div className="mt-1">
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className={`progress-bar ${completionPercentage === 100 ? 'bg-success' : 'bg-warning'}`}
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>
                <small className="text-muted">
                  {completionPercentage}% complete
                </small>
              </div>
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.status)} d-flex align-items-center gap-1`}>
          {getStatusIcon(row.original.status)}
          <span className="text-capitalize">
            {row.original.status?.replace('_', ' ') || 'Unknown'}
          </span>
        </span>
      )
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 90,
      Cell: ({ row }) => (
        <span className={`badge bg-${getPriorityColor(row.original.priority)}`}>
          {row.original.priority?.toUpperCase() || 'ROUTINE'}
        </span>
      )
    },
    {
      accessorKey: 'order_date',
      header: 'Order Date',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{formatDate(row.original.order_date)}</div>
          <small className="text-muted">
            by {row.original.ordered_by || 'Unknown'}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'expected_results_date',
      header: 'Expected',
      size: 100,
      Cell: ({ row }) => (
        <div className="text-center">
          {row.original.expected_results_date ? (
            <div>
              <div>{formatDate(row.original.expected_results_date)}</div>
              <small className="text-muted">Expected</small>
            </div>
          ) : (
            <span className="text-muted">Not set</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'last_updated',
      header: 'Last Updated',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{formatDateTime(row.original.last_updated)}</div>
          {row.original.completed_date && (
            <small className="text-success">
              <FiCheck className="me-1" />
              Completed
            </small>
          )}
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => handleViewOrder(row.original.order_id)}
            title="View Details"
          >
            <FiEye />
          </button>
          {(row.original.status === 'pending' || row.original.status === 'in_progress') && (
            <button
              className="btn btn-outline-success btn-sm"
              onClick={() => handleUploadResults(row.original.order_id)}
              title="Upload Results"
            >
              <FiUpload />
            </button>
          )}

          {/* {hasResults(row.original) && (
            <button
              className="btn btn-outline-info btn-sm"
              onClick={() => console.log('Download results for', row.original.order_id)}
              title="Download Results"
            >
              <FiDownload />
            </button>
          )} */}
          
        </div>
      )
    }
  ], [orders]);

  return (
    <div className="card mb-4">
      {/* Header with Alert */}
      <div className="card-header d-flex justify-content-between align-items-center">
        
          <h6 className="mb-0">Laboratory Orders</h6>
          <div className="d-flex align-items-center gap-2">
          {alertInfo && (
            <span className="badge bg-danger">{alertInfo.priority === 'high' ? '1' : '0'}</span>
          )}
                      </div>
          
        </div>
        <div className="card-body">
         {/* Enhanced Search and Actions Bar */}
                 <div className="row mb-3">
                   <div className="col-12">
                     <div className="d-flex align-items-center gap-2">
                       {/* Search Input */}
                       <div className="input-group" style={{maxWidth: '300px'}}>
                         <span className="input-group-text">
                           <FiSearch />
                         </span>
                         <input
                           type="text"
                           className="form-control"
                           placeholder="Search by name"
                           value={globalFilter}
                           onChange={(e) => handleGlobalFilterChange(e.target.value)}
                         />
                       </div> 
        
        
          {/* Most Recent Dropdown */}
                        <div className="dropdown" style={{position: 'relative'}}>
                          <button
                            className="btn btn-outline-secondary d-flex align-items-center gap-2"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMostRecentDropdown(!showMostRecentDropdown);
                            }}
                          >
                            Most Recent
                            <FiChevronDown />
                          </button>
                          {showMostRecentDropdown && (
                            <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', left: 0, zIndex: 1000}}>
                              <button
                                className="dropdown-item d-flex align-items-center gap-2"
                                onClick={() => handleMostRecentAction('sendMail')}
                              >
                                <FiMail />
                                Send Mail
                              </button>
                              <button
                                className="dropdown-item d-flex align-items-center gap-2"
                                onClick={() => handleMostRecentAction('sendMessage')}
                              >
                                <FiMessageSquare />
                                Send Message
                              </button>
                              <button
                                className="dropdown-item d-flex align-items-center gap-2"
                                onClick={() => handleMostRecentAction('downloadReport')}
                              >
                                <FiDownload />
                                Download report
                              </button>
                            </div>
                          )}
                        </div>
          
          {/* Filter Button */}
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={() => setShowFilterOffcanvas(true)}
          >
            <FiFilter />
            Filter
            
          </button>
          
         
          </div>
        </div>
      </div>
      

      {/* Main Table */}
      
          <MaterialReactTable
            columns={columns}
            data={filteredOrders}
            enableRowSelection
            enableGlobalFilter
            enableColumnFilters={false}
            enableColumnActions={false}
            enableSorting
            enablePagination
            enableBottomToolbar
            enableTopToolbar
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: 'order_date', desc: true }]
            }}
            state={{
              isLoading,
              rowSelection,
              globalFilter
            }}
            onRowSelectionChange={setRowSelection}
            onGlobalFilterChange={handleGlobalFilterChange}
            muiTableProps={{
              sx: { 
                '& .MuiTableCell-root': { 
                  padding: '8px 16px',
                  borderBottom: '1px solid #dee2e6'
                },
                '& .MuiTableHead-root': {
                  '& .MuiTableCell-root': {
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    color: '#495057'
                  }
                }
              }
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleViewOrder(row.original.order_id),
              sx: {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }
            })}
            renderTopToolbarCustomActions={() => (
              <div className="d-flex align-items-center gap-2">
                <FiSearch className="text-muted" />
                <span className="text-muted">
                  {filteredOrders.length} of {orders.length} orders
                </span>
              </div>
            )}
            positionGlobalFilter="left"
            muiSearchTextFieldProps={{
              placeholder: 'Search orders...',
              sx: { minWidth: '300px' },
              variant: 'outlined',
              size: 'small'
            }}
          />
        </div>
     

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setShowFilterOffcanvas(false)}></div>
          <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Laboratory Orders</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFilterOffcanvas(false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="All">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                  >
                    <option value="All">All Priorities</option>
                    <option value="routine">Routine</option>
                    <option value="urgent">Urgent</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Order Date From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.orderDate}
                    onChange={(e) => handleFilterChange('orderDate', e.target.value)}
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Order Date To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Last Update</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.lastUpdate}
                    onChange={(e) => handleFilterChange('lastUpdate', e.target.value)}
                  />
                </div>
              
              
              <div className="mt-4 d-flex gap-2">
                <button
                  className="btn btn-primary flex-fill"
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
                <button
                  className="btn btn-secondary flex-fill"
                  onClick={handleClearFilters}
                >
                  Clear All
                </button>
              </div>
              </form>
            </div>
          </div>
        </>
      )}

      

      {/* View Order Details Offcanvas */}
      {showViewOffcanvas && selectedOrder && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '800px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Laboratory Order Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-4">
                {/* Order Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">Order Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Order Number</label>
                          <div className="form-control-plaintext">
                            {selectedOrder.order_number || 'N/A'}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Sample ID</label>
                          <div className="form-control-plaintext">
                            {selectedOrder.sample_id || 'Not assigned'}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Status</label>
                          <div>
                            <span className={`badge bg-${getStatusColor(selectedOrder.status)} d-flex align-items-center gap-1 w-auto`}>
                              {getStatusIcon(selectedOrder.status)}
                              <span className="text-capitalize">
                                {selectedOrder.status?.replace('_', ' ') || 'Unknown'}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Priority</label>
                          <div>
                            <span className={`badge bg-${getPriorityColor(selectedOrder.priority)} w-auto`}>
                              {selectedOrder.priority?.toUpperCase() || 'ROUTINE'}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Order Date</label>
                          <div className="form-control-plaintext">
                            {formatDateTime(selectedOrder.order_date)}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Expected Results</label>
                          <div className="form-control-plaintext">
                            {selectedOrder.expected_results_date ? 
                              formatDate(selectedOrder.expected_results_date) : 
                              'Not set'
                            }
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Ordered By</label>
                          <div className="form-control-plaintext">
                            <div>{selectedOrder.ordered_by || 'Unknown'}</div>
                            {selectedOrder.ordered_by_email && (
                              <small className="text-muted">{selectedOrder.ordered_by_email}</small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Department</label>
                          <div className="form-control-plaintext text-capitalize">
                            {selectedOrder.department || selectedOrder.requesting_team || 'N/A'}
                          </div>
                        </div>
                        {selectedOrder.reason && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Reason</label>
                            <div className="form-control-plaintext">
                              {selectedOrder.reason}
                            </div>
                          </div>
                        )}
                        {selectedOrder.clinical_data && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Clinical Data</label>
                            <div className="form-control-plaintext">
                              {selectedOrder.clinical_data}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Details */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">Test Details</h6>
                    </div>
                    <div className="card-body">
                      {renderDetailedTestInfo(selectedOrder, hasResults(selectedOrder))}
                    </div>
                  </div>
                </div>

                
{/* Results Section - UPDATED VERSION */}
{(selectedOrder.status === 'completed' || selectedOrder._original?.status === 'completed') && (
  <div className="col-12">
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="card-title mb-0">
          <FiCheck className="me-2 text-success" />
          Results
        </h6>
        <span className="badge bg-success">
          Completed {selectedOrder.completed_date ? `on ${formatDateTime(selectedOrder.completed_date)}` : ''}
        </span>
      </div>
      <div className="card-body">
        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="alert alert-info mb-3">
            <small>
              <strong>Debug Info:</strong><br/>
              Status: {selectedOrder.status}<br/>
              Has results array: {selectedOrder._original?.results ? 'Yes' : 'No'}<br/>
              Results count: {selectedOrder._original?.results?.length || 0}<br/>
              Has selectedTests: {selectedOrder._original?.selectedTests ? 'Yes' : 'No'}
            </small>
          </div>
        )}
        
        {/* Check if we have structured results */}
        {selectedOrder._original?.results && Array.isArray(selectedOrder._original.results) && selectedOrder._original.results.length > 0 ? (
          <div className="results-container">
            <h6 className="text-primary mb-3">Test Results</h6>
            {selectedOrder._original.results.map((testResult, testIndex) => (
              <div key={testIndex} className="test-result-card border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="text-primary mb-0">
                    {testResult.testName || `Test ${testIndex + 1}`}
                  </h6>
                  {testResult.testCode && (
                    <span className="badge bg-secondary">{testResult.testCode}</span>
                  )}
                </div>
                
                {testResult.parameters && Array.isArray(testResult.parameters) && (
                  <div className="parameters-results">
                    <div className="row g-2">
                      {testResult.parameters.map((param, paramIndex) => (
                        <div key={paramIndex} className="col-md-6">
                          <div className="parameter-result p-2 border rounded bg-light">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <div className="fw-medium">{param.parameterName}</div>
                                <div className="result-value fs-5 text-primary fw-bold">
                                  {param.value} {param.unit && <span className="text-muted">{param.unit}</span>}
                                </div>
                                {param.referenceRange && (param.referenceRange.min || param.referenceRange.max) && (
                                  <small className="text-muted">
                                    Range: {param.referenceRange.min || 'N/A'} - {param.referenceRange.max || 'N/A'}
                                  </small>
                                )}
                              </div>
                              <div className="text-end">
                                {param.isAbnormal ? (
                                  <span className="badge bg-warning">Abnormal</span>
                                ) : (
                                  <span className="badge bg-success">Normal</span>
                                )}
                              </div>
                            </div>
                            {param.notes && (
                              <div className="mt-2">
                                <small className="text-info">
                                  <FiInfo className="me-1" />
                                  {param.notes}
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {testResult.generalNotes && (
                  <div className="mt-2 p-2 bg-info bg-opacity-10 rounded">
                    <small className="text-info">
                      <FiFileText className="me-1" />
                      {testResult.generalNotes}
                    </small>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Fallback: Check if selectedTests have results
          selectedOrder._original?.selectedTests && Array.isArray(selectedOrder._original.selectedTests) ? (
            <div className="results-container">
              <h6 className="text-primary mb-3">Test Results</h6>
              {selectedOrder._original.selectedTests.map((test, testIndex) => {
                const hasTestResults = test.selectedParameters && test.selectedParameters.some(param => 
                  param.result !== undefined && param.result !== null && param.result !== ''
                );
                
                if (!hasTestResults) return null;
                
                return (
                  <div key={testIndex} className="test-result-card border rounded p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="text-primary mb-0">
                        {test.testName || test.name || `Test ${testIndex + 1}`}
                      </h6>
                      {test.testCode && (
                        <span className="badge bg-secondary">{test.testCode}</span>
                      )}
                    </div>
                    
                    <div className="parameters-results">
                      <div className="row g-2">
                        {test.selectedParameters?.filter(param => 
                          param.result !== undefined && param.result !== null && param.result !== ''
                        ).map((param, paramIndex) => (
                          <div key={paramIndex} className="col-md-6">
                            <div className="parameter-result p-2 border rounded bg-light">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div className="fw-medium">{param.parameterName || param.name}</div>
                                  <div className="result-value fs-5 text-primary fw-bold">
                                    {param.result} {param.unit && <span className="text-muted">{param.unit}</span>}
                                  </div>
                                  {param.referenceRange && (param.referenceRange.min || param.referenceRange.max) && (
                                    <small className="text-muted">
                                      Range: {param.referenceRange.min || 'N/A'} - {param.referenceRange.max || 'N/A'}
                                    </small>
                                  )}
                                </div>
                                <div className="text-end">
                                  {param.isAbnormal ? (
                                    <span className="badge bg-warning">Abnormal</span>
                                  ) : (
                                    <span className="badge bg-success">Normal</span>
                                  )}
                                </div>
                              </div>
                              {param.notes && (
                                <div className="mt-2">
                                  <small className="text-info">
                                    <FiInfo className="me-1" />
                                    {param.notes}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Final fallback: Show completion message
            <div className="alert alert-info">
              <FiInfo className="me-2" />
              <strong>Order Completed</strong>
              <p className="mb-0 mt-2">
                This order has been marked as completed. Results may be available in the laboratory system 
                or may need to be uploaded manually.
              </p>
              {selectedOrder.completed_date && (
                <small className="text-muted d-block mt-2">
                  Completed on {formatDateTime(selectedOrder.completed_date)}
                  {selectedOrder.processed_by && ` by ${selectedOrder.processed_by}`}
                </small>
              )}
            </div>
          )
        )}
        
        {/* General results notes */}
        {(selectedOrder.results_notes || selectedOrder._original?.observations) && (
          <div className="mt-3 p-3 bg-light rounded">
            <h6 className="text-primary mb-2">
              <FiFileText className="me-2" />
              Additional Notes
            </h6>
            <p className="mb-0">{selectedOrder.results_notes || selectedOrder._original?.observations}</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2 justify-content-end">
                    {(selectedOrder.status === 'pending' || selectedOrder.status === 'in_progress') && (
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          handleCloseViewOffcanvas();
                          setTimeout(() => handleUploadResults(selectedOrder.order_id), 300);
                        }}
                      >
                        <FiUpload className="me-2" />
                        Upload Results
                      </button>
                    )}
                    {/* {hasResults(selectedOrder) && (
                      <button
                        className="btn btn-info"
                        onClick={() => console.log('Download results for', selectedOrder.order_id)}
                      >
                        <FiDownload className="me-2" />
                        Download Results
                      </button>
                    )} */}
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleCloseViewOffcanvas}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Upload Results Offcanvas - CORREGIDO */}
      {showResultsOffcanvas && selectedOrder && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseResultsOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '700px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Upload Results</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseResultsOffcanvas}
                disabled={isUploadingResults}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="text-primary mb-0">
                    Order: {selectedOrder.order_number}
                  </h6>
                  <span className={`badge bg-${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <small className="text-muted">
                Patient: {selectedOrder.patient_name || 'Unknown'} 
                {selectedOrder.patient_id && ` (ID: ${selectedOrder.patient_id})`}
              </small>
              </div>

              {/* Success/Error Messages */}
              {uploadSuccess && (
                <div className="alert alert-success d-flex align-items-center">
                  <FiCheck className="me-2" />
                  {uploadSuccess}
                </div>
              )}
              
              {uploadError && (
                <div className="alert alert-danger d-flex align-items-center">
                  <FiAlertTriangle className="me-2" />
                  {uploadError}
                </div>
              )}

              {/* Results Form */}
              <div className="row g-3">
                {Object.keys(resultsData).length === 0 ? (
                  <div className="col-12">
                    <div className="alert alert-warning">
                      <FiAlertTriangle className="me-2" />
                      No parameters found for this order. Please refresh and try again.
                    </div>
                  </div>
                ) : (
                  Object.entries(resultsData).map(([key, result]) => (
                    <div key={key} className="col-12">
                      <div className="card">
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-12">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="text-primary mb-1">{result.testName}</h6>
                                  <div className="fw-medium">{result.parameterName}</div>
                                </div>
                                {result.unit && (
                                  <span className="badge bg-light text-dark border">
                                    {result.unit}
                                  </span>
                                )}
                              </div>
                              
                              {result.referenceRange && (result.referenceRange.min || result.referenceRange.max) && (
                                <div className="mb-2">
                                  <small className="text-muted">
                                    <FiInfo className="me-1" />
                                    Reference Range: {result.referenceRange.min || 'N/A'} - {result.referenceRange.max || 'N/A'}
                                  </small>
                                </div>
                              )}
                            </div>
                            
                            <div className="col-md-8">
                              <label className="form-label">Result Value</label>
                              <input
                                type="text"
                                className="form-control"
                                value={result.value || ''}
                                onChange={(e) => handleResultChange(key, 'value', e.target.value)}
                                placeholder="Enter result value"
                                disabled={isUploadingResults}
                              />
                            </div>
                            
                            <div className="col-md-4">
                              <label className="form-label">Status</label>
                              <select
                                className="form-select"
                                value={result.status || 'normal'}
                                onChange={(e) => handleResultChange(key, 'status', e.target.value)}
                                disabled={isUploadingResults}
                              >
                                <option value="normal">Normal</option>
                                <option value="abnormal">Abnormal</option>
                                <option value="critical">Critical</option>
                                <option value="pending">Pending</option>
                              </select>
                            </div>
                            
                            <div className="col-12">
                              <label className="form-label">Notes (Optional)</label>
                              <textarea
                                className="form-control"
                                rows="2"
                                value={result.notes || ''}
                                onChange={(e) => handleResultChange(key, 'notes', e.target.value)}
                                placeholder="Additional notes or observations"
                                disabled={isUploadingResults}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                
                {/* General Results Notes */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">General Notes</h6>
                    </div>
                    <div className="card-body">
                      <textarea
                        className="form-control"
                        rows="3"
                        value={generalNotes}
                        onChange={(e) => setGeneralNotes(e.target.value)}
                        placeholder="General observations, recommendations, or additional notes about the results"
                        disabled={isUploadingResults}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2 justify-content-end pt-3 border-top">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleCloseResultsOffcanvas}
                      disabled={isUploadingResults}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleSubmitResults}
                      disabled={isUploadingResults || Object.keys(resultsData).length === 0}
                    >
                      {isUploadingResults ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FiCheck className="me-2" />
                          Submit Results
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LaboratoryTreatments;